import TokenAvatar from '@components/TokenAvatar'
import { logger } from '@plug-core/logger'
import { useCurrentWallet } from '@plug-core/state'
import { HttpAgent } from '@dfinity/agent'
import useConfig from '@plug-core/hooks/useConfig'
import { SwapProvider, SwapTokenPair } from '@plug-core/interfaces/swapInterface'
import {
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import ICPSwap from '@plug-core/clients/ICPSwap'
import getAgent from '@plug-core/utils/getAgent'
import { subaccountFromPrincipal } from '@plug-core/utils/subaccount'
import getTokenBalance from '@plug-core/utils/tokenActions/tokenBalance'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { HiOutlineArrowUturnDown } from 'react-icons/hi2'
import getTokenMetadata from '@plug-core/utils/tokenActions/getMetadata'
import { useTranslation } from 'react-i18next'
import Touchable from '@components/Touchable'
import { getIdentity } from '@plug-core/handlers/walletActions/wallet'
import { getPrettyDecimals } from '@plug-core/utils/validationHelper'

interface PairFallbackState {
  pair: SwapTokenPair
}

interface Balance {
  canisterPrincipal: string
  tokenStandard: string
  balance: number
  type: 'undeposited' | 'unwithdrawn'
}

const ICPSwapFallback = () => {
  const [keyringAgent, setKeyringAgent] = useState<HttpAgent>()

  const [balances, setBalances] = useState<Balance[]>([])
  const { pair } = useLocation().state as PairFallbackState

  const { currentWallet } = useCurrentWallet()
  const { tokens } = useConfig()

  const [balanceInWithdraw, setBalanceInWithdraw] = useState<Balance>()
  const [refreshBalances, setRefreshBalances] = useState<number>()

  const { t } = useTranslation()

  useEffect(() => {
    if (!currentWallet) return

    getIdentity(currentWallet)
      .then((identity) => {
        const agent = getAgent({ identity })

        setKeyringAgent(agent)
      })
      .catch(logger.error)
  }, [currentWallet])

  const fallback = useCallback(
    async (balance: Balance) => {
      if (!keyringAgent) return

      if (pair.provider === SwapProvider.ICPSWAP) {
        const icpSwapService = new ICPSwap(keyringAgent)

        const tokenMetadata = await getTokenMetadata(
          balance.canisterPrincipal,
          balance.tokenStandard,
          getAgent()
        )

        const poolActor = icpSwapService.getPoolActor(pair.swapCanisterPrincipal)

        if (balance.type === 'undeposited') {
          await poolActor.deposit({
            fee: BigInt(tokenMetadata.fee),
            token: balance.canisterPrincipal,
            amount: BigInt(balance.balance - tokenMetadata.fee),
          })
        }

        await poolActor.withdraw({
          fee: BigInt(tokenMetadata.fee),
          token: balance.canisterPrincipal,
          amount: BigInt(
            balance.balance - (balance.type === 'undeposited' ? 2 : 1) * tokenMetadata.fee
          ),
        })
      }
    },
    [keyringAgent, pair]
  )

  const performFallback = useCallback(
    (balance: Balance) => {
      setBalanceInWithdraw(balance)

      fallback(balance)
        .then(() => {
          setRefreshBalances(Date.now())
        })
        .catch((e) =>
          logger.error('Failed to perform ICPSwap fallback', {
            originalError: e,
          })
        )
        .finally(() => setBalanceInWithdraw(undefined))
    },
    [fallback]
  )

  useEffect(() => {
    const agent = getAgent()

    setBalances([])

    if (pair.provider === SwapProvider.ICPSWAP) {
      const tokens = [pair.token0, pair.token1]
      tokens.map((token) => {
        getTokenBalance(
          token.principal,
          token.standard,
          agent,
          pair.swapCanisterPrincipal,
          subaccountFromPrincipal(currentWallet)
        ).then((balance) => {
          setBalances((currentValue) => [
            ...currentValue,
            {
              canisterPrincipal: token.principal,
              tokenStandard: token.standard,
              balance: Number(balance),
              type: 'undeposited',
            },
          ])
        })
      })

      const icpSwapService = new ICPSwap(agent)

      icpSwapService.getPoolUnusedBalance(pair, currentWallet).then((unusedBalances) => {
        setBalances((currentValue) => [
          ...currentValue,
          {
            canisterPrincipal: pair.token0.principal,
            tokenStandard: pair.token0.standard,
            balance: unusedBalances.balance0,
            type: 'unwithdrawn',
          },
          {
            canisterPrincipal: pair.token1.principal,
            tokenStandard: pair.token1.standard,
            balance: unusedBalances.balance1,
            type: 'unwithdrawn',
          },
        ])
      })
    }
  }, [pair, currentWallet, refreshBalances])

  const renderBalanceRow = (balance: Balance) => {
    const tokenDetails = tokens.find((token) => token.address === balance.canisterPrincipal)

    return (
      <ListItem>
        <ListItemAvatar>
          <TokenAvatar logo={tokenDetails?.logo} />
        </ListItemAvatar>
        <ListItemText
          primary={getPrettyDecimals(balance.balance, Number(tokenDetails?.decimals || 8))}
          secondary={tokenDetails?.symbol}
        />
        <ListItemSecondaryAction>
          {balanceInWithdraw === balance && <CircularProgress size={20} thickness={5} />}
          {!balanceInWithdraw && (
            <IconButton onClick={() => performFallback(balance)}>
              <HiOutlineArrowUturnDown color="white" />
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    )
  }

  return (
    <Stack mx={2}>
      <List>{balances.map(renderBalanceRow)}</List>
      {pair.provider === SwapProvider.ICPSWAP && (
        <Touchable
          onClick={() => window.open('https://app.icpswap.com/swap/withdraw?type=pair', '_blank')}
        >
          <Typography
            color={(theme) => theme.palette.rainbow.blue}
            textAlign="center"
            fontWeight="bold"
            fontSize={13}
          >
            {t('swap.fallbackICPSwap')}
          </Typography>
        </Touchable>
      )}
    </Stack>
  )
}

export default ICPSwapFallback
